import type { members as memberContracts } from '@personal-blog/contracts';
import { Avatar, type AvatarSize } from '@viquard/doc-ui';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

export function MemberAvatar(props: {
  name: string;
  design?: memberContracts.ProfileAvatarDesign | null;
  size?: AvatarSize;
  className?: string;
}): ReactNode {
  const { name, design = null, size = 'md', className } = props;
  const initials = design?.initials ?? deriveInitials(name);
  const shape = design?.shape ?? 'circle';
  const accent = design?.accent ?? 'iris';
  const style = design?.style ?? 'solid';
  const frame = design?.frame ?? 'shadow';
  const backdrop = design?.backdrop ?? 'gradient';
  const fit = design?.fit ?? 'cover';
  const imageUrl =
    design?.source === 'image' && typeof design.imageUrl === 'string' && design.imageUrl.length > 0
      ? design.imageUrl
      : null;
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  useEffect(() => {
    setFailedImageUrl(null);
  }, [imageUrl]);
  const showImage = imageUrl !== null && failedImageUrl !== imageUrl;
  const scale = Math.min(180, Math.max(80, design?.scale ?? 100)) / 100;
  const positionX = Math.min(100, Math.max(0, design?.positionX ?? 50));
  const positionY = Math.min(100, Math.max(0, design?.positionY ?? 50));
  const imageStyle: CSSProperties = {
    objectFit: fit,
    objectPosition: `${String(positionX)}% ${String(positionY)}%`,
    transform: `scale(${String(scale)})`,
  };
  return (
    <Avatar
      name={name}
      size={size}
      shape={shape}
      initials={initials}
      {...(className === undefined ? {} : { className })}
    >
      <span
        className="member-designed-avatar"
        data-accent={accent}
        data-style={style}
        data-source={showImage ? 'image' : 'initials'}
        data-frame={frame}
        data-backdrop={backdrop}
        aria-hidden="true"
      >
        {showImage ? (
          <img
            className="member-designed-avatar__image"
            src={imageUrl}
            alt=""
            draggable={false}
            onError={() => {
              setFailedImageUrl(imageUrl);
            }}
            style={imageStyle}
          />
        ) : (
          <span className="member-designed-avatar__initials">{initials}</span>
        )}
      </span>
    </Avatar>
  );
}

function deriveInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const firstPart = parts[0] ?? '';
  const lastPart = parts[parts.length - 1] ?? '';
  if (parts.length === 1) return Array.from(firstPart)[0] ?? '?';
  const first = Array.from(firstPart)[0] ?? '';
  const last = Array.from(lastPart)[0] ?? '';
  return `${first}${last}`.toUpperCase();
}
